import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import { Button, Tooltip } from '@mui/material'
import classNames from 'classnames'
import React from 'react'
import useClubContext from '../../../hooks/club/clubContext'
import useSizeGuide from '../../../hooks/sizeGuide/sizeGuide'
import { sizeLabels } from '../../../hooks/sizeGuide/sizeGuideConstants'
import { ProductInterface, ProductSizeItemInterface, SIZE_PRESET_NONE } from '../../../interfaces/ProductInterface'
import FormattedMessage from '../../FormattedMessage'
import LoadingSpinner, { Sizes } from '../../LoadingSpinner'
import styles from './ProductVariant.module.scss'

type Props = {
  addingToCart: boolean
  sizes: ProductSizeItemInterface[]
  variantId?: number
  classes?: {
    root?: string
  }
  setVariantId: (variantId: number, errorCallback?: () => void) => void
  getVariant: () => ProductInterface | undefined
}

const MINIMUM_PRODUCT_STOCK = 4

const ProductVariant = (props: Props) => {
  const { variantId, classes, setVariantId, sizes, addingToCart, getVariant } = props
  const lowStockRef = React.useRef<number>(0)
  const sizeGuide = useSizeGuide()
  const clubContext = useClubContext()

  const sizeCountriesHint = React.useMemo(() => {
    const variant = getVariant()
    if (!variant) {
      return null
    }
    const size = sizeGuide.getSizeById(variant.size.id)
    if (!size) {
      return null
    }
    return (
      <div className={styles['countries-sizes']}>
        {sizeLabels
          .filter(s => s.isCountryLabel && size[s.key])
          .map((item, idx, arr) => (
            <div key={item.label} className={styles['countries-sizes__item']}>
              <span className={styles['countries-sizes__country']}>{item.label}</span>
              <span className={styles['countries-sizes__size']}>{size[item.key]}</span>
              {idx !== arr.length - 1 ? ',' : ''}
            </div>
          ))}
      </div>
    )
  }, [variantId])

  const onVariantChange = (value: string | number) => {
    setVariantId(value as number)
  }

  const renderVariants = () => {
    lowStockRef.current = 0
    if (Array.isArray(sizes) && sizes.length > 0) {
      return sizes.map(_size => {
        const label = _size && _size.preset !== SIZE_PRESET_NONE ? sizeGuide.getSizeLabel(_size.id) || _size.label : <AddShoppingCartIcon />
        const available = _size.availablity > 0 || clubContext.isInClubFlow
        const content = (
          <Button
            classes={{ root: styles['variant'] }}
            color={_size.productId == variantId ? 'primary' : 'secondary'}
            variant="contained"
            key={`var-${_size.id}`}
            onClick={onVariantChange.bind(this, _size.productId)}
            disabled={addingToCart || !available}
          >
            {addingToCart && _size.productId == variantId ? <LoadingSpinner size={Sizes.BUTTON} /> : label}
          </Button>
        )

        if (_size.availablity <= MINIMUM_PRODUCT_STOCK && available && _size.productId === variantId) {
          lowStockRef.current = _size.availablity
        }

        return !available ? (
          <Tooltip
            key={`var-${_size.id}`}
            placement="top"
            title={<FormattedMessage id="ProductVariant.NotAvailableVariant" defaultMessage="This size is not available." />}
          >
            <div>{content}</div>
          </Tooltip>
        ) : (
          content
        )
      })
    }
    return null
  }

  return (
    <form autoComplete="off" className={classNames(styles['product-variant'], classes?.root!)}>
      <FormattedMessage
        id="ProductVariant.ChooseSize"
        description="ProductVariant.ChooseSize"
        defaultMessage="Choose size:"
        className={styles['size-label']}
      />
      {renderVariants()}
      {sizeCountriesHint}
      {lowStockRef.current ? (
        <FormattedMessage className={styles['qty-alert']} id="ProductVariant.LowStockAlert" defaultMessage="Low stock" />
      ) : null}
    </form>
  )
}

export default ProductVariant
